/**
 * Review Text Overlay Composable
 *
 * Single source of truth for review card text layout and compositing.
 * Based on ReviewCard.vue layout, supports both:
 * - Web: CSS/HTML responsive overlay
 * - Export: Canvas-based compositing for social sharing
 */

/**
 * Ensure LandformWeb font is loaded before using in canvas
 * @returns {Promise<boolean>} - True if font is loaded/available
 */
const ensureFontLoaded = async () => {
  try {
    // Check if the font is already loaded
    if (document.fonts.check('16px LandformWeb')) {
      return true
    }

    // Try to load the font
    await document.fonts.load('16px LandformWeb')
    return document.fonts.check('16px LandformWeb')
  } catch (error) {
    console.warn('Could not load LandformWeb font:', error)
    return false
  }
}

/**
 * Get font stack with fallback - ensures LandformWeb if loaded, otherwise uses fallbacks
 * @param {number} size - Font size
 * @param {string} weight - Font weight (e.g., 'bold', 'normal')
 * @param {boolean} fontLoaded - Whether LandformWeb is loaded
 * @returns {string} - Complete font declaration
 */
const getFontString = (size, weight = 'normal', fontLoaded = false) => {
  const baseStack = 'system-ui, -apple-system, sans-serif'
  const fontFamily = fontLoaded ? `LandformWeb, ${baseStack}` : baseStack
  return `${weight} ${size}px ${fontFamily}`
}

// Spectra "S" logo only (first character from logotype) - viewBox: 0 0 226 243
const SPECTRA_S_LOGO_PATH =
  'M11.6,225.3c-4-2-5.3-4-5.3-9.6v-48.1c0-9,2.7-10.3,9-6,18.2,12.6,53.1,21.6,91.2,21.6s56.4-5,56.4-18.6-30.5-12.9-71.3-15.3C48.4,147,0,136,0,74.3S43.5,0,109.8,0s75,7.3,94.2,16.9,5.3,4,5.3,9.6v48.1c0,9-2.7,10.3-9,6-18.2-12.6-46.5-20.9-90.6-20.9s-46.8,4.3-46.8,17.9,24.9,12.3,54.4,13.9c51.8,3,108.5,10.6,108.5,76.3s-44.8,75-119.4,75-75.7-8-94.9-17.6Z'

// Spectra full logotype SVG path data - viewBox: 0 0 1379 303.9
const SPECTRA_LOGO_PATH =
  'M11.6,225.3c-4-2-5.3-4-5.3-9.6v-48.1c0-9,2.7-10.3,9-6,18.2,12.6,53.1,21.6,91.2,21.6s56.4-5,56.4-18.6-30.5-12.9-71.3-15.3C48.4,147,0,136,0,74.3S43.5,0,109.8,0s75,7.3,94.2,16.9,5.3,4,5.3,9.6v48.1c0,9-2.7,10.3-9,6-18.2-12.6-46.5-20.9-90.6-20.9s-46.8,4.3-46.8,17.9,24.9,12.3,54.4,13.9c51.8,3,108.5,10.6,108.5,76.3s-44.8,75-119.4,75-75.7-8-94.9-17.6Z M255.8,297.3V65c0-3.7,3-6.6,6.6-6.6h46.5c3.6,0,6.6,3,6.6,6.6v6.6c12.3-11.9,28.9-18.6,48.8-18.6,55.4,0,88.6,37.8,88.6,95.2s-33.2,94.2-88.6,94.2-36.5-6.6-48.8-18.3v73c0,3.6-3,6.6-6.6,6.6h-46.5c-3.6,0-6.6-3-6.6-6.6ZM393.2,148c0-25.2-15.3-41.8-38.8-41.8s-38.8,16.6-38.8,41.8,15.3,41.5,38.8,41.5,38.8-16.3,38.8-41.5Z M476.1,148c0-60.1,37.2-94.9,97.5-94.9s87.9,40.1,87.9,108.5-3,6.3-6.6,6.3h-116.8c5.3,17.6,18.2,28.5,42.1,28.5s43.5-5.3,60.4-12.9c3.6-1.7,7.6.3,7.6,4.3v38.5c0,2.3-1.3,4.6-3.7,5.6-15.6,6.6-42.5,10.9-67.7,10.9-59.7,0-100.9-37.5-100.9-94.9ZM604.2,130.1c0-16.6-15.3-27.2-30.5-27.2s-30.5,10.3-35.5,27.2h66Z M684.9,148c0-57.4,41.8-94.9,105.5-94.9s47.8,4.3,59.7,11.3c2.3,1.3,4.3,3,4.3,5.6v48.1c0,4-4,6-7.6,4-13.9-7.6-34.2-12.6-56.4-12.6s-45.8,15.3-45.8,38.5,18.2,38.5,45.8,38.5,40.1-5.3,57.1-12.9c3.6-1.7,7.6.3,7.6,4.3v48.4c0,2.3-1.3,4.6-3.7,5.6-15.6,6.6-37.8,10.9-61.1,10.9-63.7,0-105.5-37.5-105.5-94.9Z M905.8,157.6v-52.8h-27.5c-3.7,0-6.6-3-6.6-6.6v-33.2c0-3.7,3-6.6,6.6-6.6h27.5V11.9c0-3.7,3-6.6,6.6-6.6h46.5c3.7,0,6.6,3,6.6,6.6v46.5h46.5c3.6,0,6.6,3,6.6,6.6v33.2c0,3.6-3,6.6-6.6,6.6h-46.5v52.8c0,36.2,20.6,35.8,52.8,29.5,4-.7,8,1.7,8,5.6l.7,36.8c0,2.3-1.3,4.6-3.7,5.6-10.9,4.6-26.2,7.6-42.1,7.6-49.4,0-75.3-29.2-75.3-85.3Z M1050.2,230.6V65c0-3.7,3-6.6,6.6-6.6h46.5c3.6,0,6.6,3,6.6,6.6v11.3c13.9-13.6,32.5-21.6,55.1-22.9,4-.3,7,2.7,7,6.6v46.1c0,3.6-3,6.6-7,6.6-38.5,1-55.1,10.9-55.1,34.8v83c0,3.7-3,6.6-6.6,6.6h-46.5c-3.6,0-6.6-3-6.6-6.6Z M1181.9,147.3c0-57.1,33.2-94.2,88.6-94.2s36.5,6.6,48.8,18.2v-6.3c0-3.7,3-6.6,6.6-6.6h46.5c3.6,0,6.6,3,6.6,6.6v165.9c0,3.6-3,6.6-6.6,6.6h-46.5c-3.7,0-6.6-3-6.6-6.6v-7c-12.3,11.9-28.9,18.6-48.8,18.6-55.4,0-88.6-37.8-88.6-95.2ZM1319.3,147.7c0-25.2-15.3-41.5-38.8-41.5s-38.8,16.3-38.8,41.5,15.3,41.8,38.8,41.8,38.8-16.6,38.8-41.8Z'

/**
 * Calculate text sizes based on container width (for responsive web)
 * Matches ReviewCard.vue responsive sizing
 */
export function getTextSizes(containerWidth) {
  // Base sizes (mobile-first, matching ReviewCard)
  let usernameSize = '0.625rem' // text-2xs
  let titleSize = '1rem' // base
  let subtitleSize = '0.625rem' // text-2xs
  let scoreSize = '1rem' // base

  // Responsive breakpoints matching ReviewCard @container queries
  if (containerWidth >= 640) {
    // @xs
    usernameSize = '0.75rem' // text-xs
    titleSize = '1.125rem' // text-lg
    scoreSize = '1.125rem' // text-lg
  }
  if (containerWidth >= 768) {
    // @sm
    titleSize = '1.25rem' // text-xl
    scoreSize = '1.25rem' // text-xl
  }
  if (containerWidth >= 1024) {
    // @md
    titleSize = '1.5rem' // text-2xl
    subtitleSize = '0.75rem' // text-xs
    scoreSize = '1.5rem' // text-2xl
  }
  if (containerWidth >= 1280) {
    // @lg
    titleSize = '1.875rem' // text-3xl
    subtitleSize = '0.875rem' // text-sm
    scoreSize = '1.875rem' // text-3xl
  }
  if (containerWidth >= 1536) {
    // @xl
    titleSize = '2.25rem' // text-4xl
    scoreSize = '2.25rem' // text-4xl
  }

  return {
    username: usernameSize,
    title: titleSize,
    subtitle: subtitleSize,
    score: scoreSize
  }
}

/**
 * Calculate text sizes for canvas rendering (fixed sizes)
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {number} scale - Scale factor (for export, typically 1)
 * @returns {Object} Text size configuration
 */
export function getCanvasTextSizes(width, height, scale = 1) {
  // Base sizes calculated from ReviewCard's responsive sizes
  // For a typical 1200px wide card, we use @md sizes as baseline
  const baseWidth = 1200
  const baseScale = width / baseWidth

  // Minimum readable sizes (ensures legibility at small scales)
  const minUsername = Math.max(8, 16 * baseScale * scale)
  const minTitle = Math.max(12, 32 * baseScale * scale)
  const minSubtitle = Math.max(8, 16 * baseScale * scale)
  const minScore = Math.max(16, 40 * baseScale * scale)

  // Boost for very small sizes to maintain readability
  const smallSizeBoost = scale < 0.5 ? 1.3 : scale < 0.75 ? 1.15 : 1

  return {
    username: minUsername * smallSizeBoost,
    title: minTitle * smallSizeBoost,
    subtitle: minSubtitle * smallSizeBoost,
    score: minScore * smallSizeBoost,
    padding: Math.max(16, 24 * baseScale * scale)
  }
}

/**
 * Draw Spectra logo on canvas
 */
function drawSpectraLogo(ctx, x, y, targetWidth, fillColor = 'white') {
  const originalWidth = 1379
  const originalHeight = 303.9
  const scale = targetWidth / originalWidth

  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)

  const path = new Path2D(SPECTRA_LOGO_PATH)
  ctx.fillStyle = fillColor
  ctx.fill(path)

  ctx.restore()

  return originalHeight * scale
}

/**
 * Draw Spectra "S" logo on canvas
 */
function drawSpectraSLogo(ctx, x, y, size, fillColor = 'white') {
  const originalWidth = 226
  const originalHeight = 243
  const scale = size / Math.max(originalWidth, originalHeight)

  ctx.save()
  ctx.translate(
    x - (originalWidth * scale) / 2,
    y - (originalHeight * scale) / 2
  )
  ctx.scale(scale, scale)

  const path = new Path2D(SPECTRA_S_LOGO_PATH)
  ctx.fillStyle = fillColor
  ctx.fill(path)

  ctx.restore()
}

/**
 * Render text overlay on canvas matching ReviewCard layout
 *
 * Layout (from ReviewCard.vue):
 * - Username above title (left side, smaller)
 * - Title with year/subtitle on left (bottom)
 * - Score on right (bottom)
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} options - Rendering options
 * @param {number} options.width - Canvas width
 * @param {number} options.height - Canvas height
 * @param {string} options.username - Username (e.g., "username")
 * @param {string} options.title - Title text
 * @param {string} options.subtitle - Subtitle/year text
 * @param {number|string} options.score - Score value
 * @param {number} options.scale - Scale factor (default 1)
 * @param {boolean} options.showLogo - Show Spectra logo (default false)
 * @param {boolean} options.showDomain - Show domain/URL (default false)
 * @param {boolean} options.isPreview - Whether this is a preview (affects 4:5 scaling)
 */
export async function renderTextOverlay(ctx, options) {
  const {
    width,
    height,
    username = null,
    title = null,
    subtitle = null,
    score = null,
    scale = 1,
    showLogo = false,
    showDomain = false,
    isPreview = false
  } = options

  // Ensure font is loaded before drawing
  const fontLoaded = await ensureFontLoaded()

  // Detect aspect ratio and apply 4:5 specific adjustments
  const aspectRatio = width / height
  const is45Format = aspectRatio < 1.0 // 4:5 is ~0.8

  // Apply format-specific text scaling
  let textScale = scale
  if (is45Format) {
    if (isPreview) {
      textScale = scale * 0.3
    } else {
      textScale = scale * 1.85
    }
  } else {
    // 16:9 format
    textScale = scale * 1.6
  }

  const sizes = getCanvasTextSizes(width, height, textScale)
  let padding = sizes.padding

  // Add extra padding for 4:5 final images
  if (is45Format && !isPreview) {
    padding = padding * 1.5
  }

  // Draw gradient overlay for text readability (matching ReviewCard's opacity-40 background)
  // Gradient starts at 60% height, goes to bottom
  const gradient = ctx.createLinearGradient(0, height * 0.6, 0, height)
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.7)')
  ctx.fillStyle = gradient
  ctx.fillRect(0, height * 0.6, width, height * 0.4)

  // Text shadow for readability
  const shadowColor = 'rgba(0, 0, 0, 0.5)'
  const shadowBlur = Math.max(2, 4 * scale)
  const shadowOffset = Math.max(1, 2 * scale)

  // Calculate bottom positioning (matching ReviewCard's bottom-0 flex items-end)
  // Elements are positioned from bottom up
  const bottomY = height - padding

  // Draw score first (bottom right) to know its height for title truncation
  let scoreTextWidth = 0
  if (score !== null && score !== undefined) {
    ctx.save()
    const scoreText = Number(score).toFixed(1)
    ctx.font = getFontString(sizes.score, 'bold', fontLoaded)
    ctx.fillStyle = 'white'
    ctx.textAlign = 'right'
    ctx.textBaseline = 'alphabetic'
    ctx.shadowColor = shadowColor
    ctx.shadowBlur = shadowBlur
    ctx.shadowOffsetX = shadowOffset
    ctx.shadowOffsetY = shadowOffset
    scoreTextWidth = ctx.measureText(scoreText).width
    ctx.fillText(scoreText, width - padding, bottomY)
    ctx.restore()
  }

  // Draw title (bottom left, same baseline as score)
  if (title) {
    ctx.save()
    ctx.font = getFontString(sizes.title, '600', fontLoaded)
    ctx.fillStyle = 'white'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
    ctx.shadowColor = shadowColor
    ctx.shadowBlur = shadowBlur
    ctx.shadowOffsetX = shadowOffset
    ctx.shadowOffsetY = shadowOffset

    // Truncate title if too long (leave space for score on right + gap + subtitle)
    let displayTitle = title
    const gap = Math.max(24, 48 * textScale) // Gap between title and score (matching ReviewCard gap-6)
    // Reserve space for subtitle if it exists
    const subtitleSpace = subtitle ? Math.max(60, 120 * textScale) : 0
    const maxTitleWidth =
      width - padding * 2 - scoreTextWidth - gap - subtitleSpace
    if (
      ctx.measureText(displayTitle).width > maxTitleWidth &&
      displayTitle.length > 10
    ) {
      while (
        ctx.measureText(displayTitle + '...').width > maxTitleWidth &&
        displayTitle.length > 10
      ) {
        displayTitle = displayTitle.slice(0, -1)
      }
      displayTitle = displayTitle + '...'
    }

    const titleY = bottomY
    ctx.fillText(displayTitle, padding, titleY)

    // Draw year next to title (smaller, lower contrast, same baseline)
    if (subtitle) {
      const titleWidth = ctx.measureText(displayTitle).width
      ctx.font = getFontString(sizes.subtitle, '600', fontLoaded)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
      ctx.textBaseline = 'alphabetic'
      const yearSpacing = Math.max(4, 8 * textScale)
      const yearX = padding + titleWidth + yearSpacing
      ctx.fillText(subtitle, yearX, titleY)
    }
    ctx.restore()
  }

  // Draw username (above title, left side)
  // Positioned above title with small gap (matching ReviewCard flex-col layout)
  if (username) {
    ctx.save()
    ctx.font = getFontString(sizes.username, '600', fontLoaded)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
    ctx.shadowColor = shadowColor
    ctx.shadowBlur = shadowBlur
    ctx.shadowOffsetX = shadowOffset
    ctx.shadowOffsetY = shadowOffset
    // Position username above title (small gap, matching ReviewCard spacing)
    const usernameY = bottomY - sizes.title - Math.max(2, 4 * textScale)
    ctx.fillText(`@${username}`, padding, usernameY)
    ctx.restore()
  }

  // Draw domain/URL (top left corner, if enabled)
  if (showDomain) {
    ctx.save()
    ctx.font = getFontString(sizes.subtitle, 'normal', fontLoaded)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    // Position at top left corner
    ctx.fillText('spectra.reviews', padding, padding)
    ctx.restore()
  }

  // Draw Spectra logo (top right, if enabled)
  if (showLogo) {
    // Scale logo based on aspect ratio and preview mode
    const aspectScale = aspectRatio > 1.5 ? 1.4 : 1.3

    // Normalize logo size between preview formats
    let logoScale = scale
    if (isPreview && aspectRatio > 1.5) {
      // For 16:9 preview, reduce logo scale to match 4:5 size
      logoScale = scale * 0.66 // Compensate for the 4.5x preview scale
    }

    const logoWidth = Math.max(50, 200 * logoScale * aspectScale)
    drawSpectraLogo(
      ctx,
      width - padding - logoWidth,
      padding,
      logoWidth,
      'rgba(255, 255, 255, 0.7)'
    )
  }
}

/**
 * Get CSS classes for web overlay matching ReviewCard
 * Returns object with class strings for each element
 * These can be used in ReviewCard.vue to ensure consistency
 */
export function getWebOverlayClasses() {
  return {
    container:
      'absolute bottom-0 flex items-end justify-between w-full gap-6 px-4 py-1 md:py-2',
    username:
      'text-2xs @lg:text-xs font-semibold truncate text-white/80 hover:text-white/95 hover:underline',
    titleContainer: 'flex items-end gap-3 truncate',
    titleWrapper: 'relative z-10 flex flex-col justify-center min-w-0',
    title:
      'font-semibold leading-none tracking-tight text-white truncate cursor-pointer @xs:text-lg @sm:text-xl @md:text-2xl @lg:text-3xl @xl:text-4xl hover:underline',
    subtitle:
      'ml-1 @lg:ml-2 text-2xs @md:text-xs @lg:text-sm font-semibold text-white/80',
    score:
      'font-bold text-white @xs:text-lg @sm:text-xl @md:text-2xl @lg:text-3xl @xl:text-4xl'
  }
}

export default {
  getTextSizes,
  getCanvasTextSizes,
  renderTextOverlay,
  getWebOverlayClasses
}
